import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ItemCategoryVariance } from '@common/services/commondata';
import { oDataService } from '@common/services/odata';
import { Pagination } from '@common/types/pagination.types';
import { environment } from 'environments/environment';
import { BehaviorSubject, forkJoin, Observable, tap, map, filter } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class ItemCategoryVarianceService {

    // Private
    private _filters: BehaviorSubject<any> = new BehaviorSubject({});
    private _pagination: BehaviorSubject<Pagination | null> = new BehaviorSubject(null);
    private _product: BehaviorSubject<any | null> = new BehaviorSubject(null);
    private _products: BehaviorSubject<any[] | null> = new BehaviorSubject(null);
    private _itemCategoryVariance: BehaviorSubject<ItemCategoryVariance[]> = new BehaviorSubject<ItemCategoryVariance[]>(null);
    private controller: string = 'ItemCategoryVarianceManagement';
    /**
     * Constructor
     */
    constructor(
        private _httpClient: HttpClient,
        private _oDataService: oDataService,
    ) {
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    /**
     * Set BehaviorSubject ItemCategory Variance
     * @param value
     */
    setItemCategoryVariance$(value: ItemCategoryVariance[]) {
        this._itemCategoryVariance.next(value);
    }

    /**
     * Getter for filters
     */
    get filters() {
        return this._filters.value;
    }

    /**
     * Setter for filters
     */
    set filters(values: any) {
        this._filters.next(values);
    }

    /**
     * Getter for Pagination
     */
    get pagination() {
        return this._pagination.value;
    }

    /**
     * Setter for Pagination
     */
    set pagination(values) {
        this._pagination.next(values);
    }

    /**
     * Getter for pagination
     */
    get pagination$(): Observable<Pagination> {
        return this._pagination.asObservable();
    }

    /**
     * Getter for product
     */
    get product$(): Observable<any> {
        return this._product.asObservable();
    }

    /**
     * Getter for products
     */
    get products$(): Observable<any[]> {
        return this._products.asObservable();
    }

    /**
     * Getter for Observable ItemCategory Variance
     */
    get itemCategoryVariance$(): Observable<ItemCategoryVariance[]> {
        return this._itemCategoryVariance.asObservable();
    }

    /**
     * Getter for ItemCategory Variance
     */
    get itemCategoryVariance(): ItemCategoryVariance[] {
        return this._itemCategoryVariance.value;
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Get products
     * @param reload
     */
    getProductsWithPagination(reload?: boolean): Observable<any> {
        if (reload) {
            this.pagination = {
                length: 0,
                page: 0,
                size: 100,
                sort: '',
                order: 'asc'
            }
            this.filters = {};
        }
        return forkJoin({
            products: this.getProducts(this.filters, this.pagination,),
        }).pipe(
            tap((response) => {
                this._pagination.next({ ...this.pagination, length: response.products["@odata.count"] });
                this._products.next(response.products["value"]);
            })
        );
    }

    /**
     * Get products
     *
     * @param filters
     * @param pagination
     */
    getProducts(filters, pagination) {
        return this._httpClient.get<any[]>(`${environment.oDataUrl}/odata/` + this.controller + this._oDataService.setFilters(filters, pagination));
    }

    getCommonData() {
        return this.getProducts({ isActive: true }, null).pipe(
             map((response) => {
                 this._itemCategoryVariance.next(response["value"]);
                 return response["value"];
             })
         );
     }
     getOrLoadItemCategoryVariance(): Observable<ItemCategoryVariance[]> {
        if (!this._itemCategoryVariance.value) {
            return this.getCommonData().pipe(
                map(() => this._itemCategoryVariance.value), // emit the loaded value
                filter(val => !!val)              // ensure not null
            );
        }
        return this._itemCategoryVariance.asObservable();
     }
    
    GetAllLevelOneCategories(): Observable<any> {
        return this._httpClient.get<any[]>(
            `${environment.oDataUrl}/api/` + this.controller + `/levelonecategories`);
    }

    /**
    /**
     * download products
     * 
     * @param filters 
     */
    downloadProducts(filters): Observable<any[]> {
        return this._httpClient.get<any[]>(`${environment.oDataUrl}/odata/` + this.controller
            + this._oDataService.setFilters(filters, null))
            .pipe(
                map((response) => {
                    return response["value"];
                })
            );
    }

    /**
     * Upload product
     * 
     * @param products
     */
    uploadProduct(products) {
        return this._httpClient.post(`${environment.oDataUrl}/api/` + this.controller, products, {
            reportProgress: true,
            observe: 'events'
        });
    }

    updateProduct(id: any, product: any) {
        this._httpClient.put(`${environment.oDataUrl}/api/` + this.controller + `/${id}`, product).subscribe(() => {
            forkJoin({
                items: this.getProductsWithPagination(),
                commondata: this.getCommonData()
            }).subscribe();
        });
    }

    updateProducts(products: any) {
        this._httpClient.put(`${environment.oDataUrl}/api/` + this.controller + `/updatemultiple`, products)
        .subscribe(() => {

        });
    }

    createProduct(products: any) {
        this._httpClient.post(`${environment.oDataUrl}/api/` + this.controller, products).subscribe(() => {
            forkJoin({
                items: this.getProductsWithPagination(),
                commondata: this.getCommonData()
            }).subscribe();
        });
    }

    getItemCategoryVarianceById(id: number) {
        return this.itemCategoryVariance.find(x => x.id == id);
      }
}